import { IPublishMenusProps } from 'src/api/publishMenu/types';
import { IPublishDestination } from '../../types';
import { destinationId } from '../destinationId/destinationId';

/**
 * Message for when selected publish destinations cannot be resolved to an extension instance, so
 * there is no identifier to send for them. Surfaced to the user rather than publishing a partial menu.
 */
export const unresolvedPublishDestinationsMessage = (destinationNames: string[]) =>
  `Cannot publish to ${destinationNames.join(', ')}: no extension instance is configured for ` +
  `${destinationNames.length === 1 ? 'this channel' : 'these channels'}.`;

export interface IPublishSelection {
  /** The request payload — one entry per selected destination that still has selected venues. */
  channels: IPublishMenusProps[];
  /** Keys of those same destinations (see `destinationId`), for notifications and analytics. */
  channelIds: string[];
}

/**
 * Resolves the channel × venue selection into the publish payload.
 *
 * Targets are addressed by extension instance ID. A selected destination with no resolvable instance
 * — an external system with several configured addons, so no single instance to name — cannot be
 * published and throws rather than being silently dropped from the payload.
 *
 * @throws {Error} if any selected destination has no instance ID.
 */
export const buildPublishSelection = (
  publicationData: IPublishDestination[],
  selectedChannelIds: string[] = [],
  venueSelection: Record<string, boolean> = {},
): IPublishSelection => {
  const selectedChannelSet = new Set(selectedChannelIds);
  const selectedVenueIds = new Set(
    Object.entries(venueSelection)
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([venueId]) => venueId),
  );

  const selected = publicationData
    .filter((destination) => selectedChannelSet.has(destinationId(destination)))
    .map((destination) => ({
      destination,
      venues: destination.venues.map((venue) => venue.venue_id).filter((venueId) => selectedVenueIds.has(venueId)),
    }))
    .filter(({ venues }) => venues.length > 0);

  const unresolved = selected.filter(({ destination }) => !destination.extensionInstanceId);
  if (unresolved.length > 0) {
    throw new Error(unresolvedPublishDestinationsMessage(unresolved.map(({ destination }) => destination.name)));
  }

  return {
    channels: selected.map(({ destination, venues }) => ({
      instance_id: destination.extensionInstanceId as string,
      venues,
    })),
    channelIds: selected.map(({ destination }) => destinationId(destination)),
  };
};
